/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { RadioToggle, Button, Icon } from 'sarsaparilla';
import cx from 'classnames';

import { FlexDatesFlexibleBookingTab } from './FlexDatesFlexibleBookingTab';
import {
    FlexDatesCalendarTab,
    type FlexDatesCalendarTabProps,
} from './FlexDatesCalendarTab';
import type { MonthsOptionsProps } from './FlexDates.types';

const flexOptions = [
    { label: 'Choose Dates', value: 'choose-dates' },
    { label: "I'm Flexible!", value: 'flexible-booking' },
];

export interface FlexDatesPopupContentProps extends FlexDatesCalendarTabProps {
    tab: 'choose-dates' | 'flexible-booking';
    isMobile: boolean;
    setTab: React.Dispatch<React.SetStateAction<'choose-dates' | 'flexible-booking'>>;
    setError: React.Dispatch<React.SetStateAction<string>>;
    handleApplyFilters: () => void;
    onClear: () => void;
    monthsOptions: Array<MonthsOptionsProps>;
    setChecked: React.Dispatch<React.SetStateAction<Array<string>>>;
    setDuration: React.Dispatch<React.SetStateAction<string>>;
    setStartRange: React.Dispatch<React.SetStateAction<string>>;
    startRange: string;
    setEndRange: React.Dispatch<React.SetStateAction<string>>;
    endRange: string;
    checked: Array<string>;
    duration: string;
    onClose: () => void;
    monthsLimit?: number;
    generateGaProps: () => {
        clickTagCategory: string;
        clickTagAction: string;
        clickTagLabel: string;
    };
}

export const FlexDatesPopupContent = React.forwardRef<
    HTMLDivElement,
    FlexDatesPopupContentProps
>((props, ref) => {
    const {
        handleApplyFilters,
        filters,
        tab,
        setTab,
        monthsOptions,
        isMobile,
        setError,
        dates,
        setDates,
        error,
        setDuration,
        onClear,
        duration,
        checked,
        setChecked,
        onClose,
        monthsLimit,
        setStartRange,
        startRange,
        setEndRange,
        endRange,
        generateGaProps,
    } = props;

    // Find the location of the page for GA
    const isSearchPage = window.location.pathname.includes('search');
    return (
        <div ref={ref} className={cx('flex-dates-portal', { desktop: !isMobile })}>
            <div className="flex-dates-tabs">
                <div id="flex-radio-selector">
                    <RadioToggle
                        options={flexOptions}
                        value={tab}
                        clickTagCategory={isSearchPage ? 'Search' : 'Homepage'}
                        clickTagLabel="Flexible Booking"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setError('');
                            const tabValue = e.target.value as
                                'choose-dates' | 'flexible-booking';

                            setTab(tabValue);
                        }}
                    />
                    {!isMobile && (
                        <div className="sarsa-modal-content">
                            <Button
                                id="flex-dates-close-button"
                                appearance="subtle"
                                className="flex-dates-close-button sarsa-modal-close-button"
                                onClick={onClose}
                                screenReaderTextBefore="close"
                                iconAfterElement={<Icon iconName="close" />}
                            />
                        </div>
                    )}
                </div>
            </div>
            <div>
                {tab === 'choose-dates' ? (
                    <FlexDatesCalendarTab
                        onApplyFilters={handleApplyFilters}
                        filters={filters}
                        isMobile={isMobile}
                        setError={setError}
                        dates={dates}
                        setDates={setDates}
                        error={error}
                        onClear={onClear}
                        startRange={startRange}
                        setStartRange={setStartRange}
                        setEndRange={setEndRange}
                        endRange={endRange}
                        generateGaProps={generateGaProps}
                    />
                ) : tab === 'flexible-booking' ? (
                    <FlexDatesFlexibleBookingTab
                        onApplyFilters={handleApplyFilters}
                        filters={filters}
                        monthsOptions={monthsOptions}
                        setError={setError}
                        isMobile={isMobile}
                        error={error}
                        onClear={onClear}
                        duration={duration}
                        checked={checked}
                        setChecked={setChecked}
                        setDuration={setDuration}
                        monthsLimit={monthsLimit}
                    />
                ) : null}
            </div>
        </div>
    );
});
